import { IPolicy, IProduct, PolicyStatus } from 'admiral-core';

/** Stückzahl und Punkte/Bewertung für einen Status oder ein Produkt. */
export interface PolicyResultsQV {
  quantity: number;
  value: number;
}

export interface PolicyProductResults extends PolicyResultsQV {
  PLANNED: PolicyResultsQV;
  OPEN: PolicyResultsQV;
  SUBMITTED: PolicyResultsQV;
  POLICED: PolicyResultsQV;
  DECLINED: PolicyResultsQV;
  CANCELED: PolicyResultsQV;
}

/** Aggregierte Anbahnungs-Kennzahlen (Gesamt und pro Produkt). */
export interface PolicyResults {
  quantity: number;
  value: number;
  PLANNED: PolicyResultsQV;
  OPEN: PolicyResultsQV;
  OPEN70: PolicyResultsQV;
  SUBMITTED: PolicyResultsQV;
  POLICED: PolicyResultsQV;
  DECLINED: PolicyResultsQV;
  CANCELED: PolicyResultsQV;
  products: Record<string, PolicyProductResults>;
}

function emptyQV(): PolicyResultsQV {
  return { value: 0, quantity: 0 };
}

function emptyProductResults(): PolicyProductResults {
  return {
    value: 0,
    quantity: 0,
    PLANNED: emptyQV(),
    OPEN: emptyQV(),
    SUBMITTED: emptyQV(),
    POLICED: emptyQV(),
    DECLINED: emptyQV(),
    CANCELED: emptyQV(),
  };
}

/** Leeres Ergebnis-Objekt für die Anbahnungs-Auswertung. */
export function emptyPolicyResults(): PolicyResults {
  return {
    quantity: 0,
    value: 0,
    PLANNED: emptyQV(),
    OPEN: emptyQV(),
    OPEN70: emptyQV(),
    SUBMITTED: emptyQV(),
    POLICED: emptyQV(),
    DECLINED: emptyQV(),
    CANCELED: emptyQV(),
    products: {},
  };
}

/**
 * Berechnet Stückzahl und Bewertung pro Status und Produkt aus einer Anbahnungsliste
 * (gleiche Logik wie in AllPolicies).
 */
export function calculatePolicyResults(policies: IPolicy[], products: IProduct[] = []): PolicyResults {
  const results = emptyPolicyResults();
  const productMap = new Map<string, IProduct>();

  products.forEach((product) => productMap.set(product.id, product));
  policies.forEach((policy) => {
    if (policy.product) {
      productMap.set(policy.product.id, policy.product);
    }
  });

  productMap.forEach((product) => {
    results.products[product.id] = emptyProductResults();
  });

  policies.forEach((policy) => {
    if (!policy.product) {
      return;
    }

    const productId = policy.product.id;
    const quantity = Number(policy.quantity) || 0;
    const value = Number(policy.value) || 0;

    results.quantity += quantity;
    results.value += value;

    results.products[productId].quantity += quantity;
    results.products[productId].value += value;
    results.products[productId][policy.status].value += value;
    results.products[productId][policy.status].quantity += quantity;

    results[policy.status].quantity += quantity;
    results[policy.status].value += value;

    if (policy.status === PolicyStatus.OPEN && policy.probability >= 7) {
      results.OPEN70.quantity += quantity;
      results.OPEN70.value += value;
    }
  });

  return results;
}
